// Stat-holiday detection driven by the customer's address country.
//
// Returns a holiday name (string) when a date matches a CA or US public
// holiday, or null when it doesn't. Country is the 2-letter ISO code
// from S.addressComponents.country_short ('CA' / 'US'); we fall back to
// CA when unknown, since Probot is a Quebec-based vendor and most
// customers are domestic.
//
// Dates are computed at runtime — Easter via the Computus algorithm,
// floating Mondays/Thursdays via getNthDow / getLastDow. No hardcoded
// year tables to maintain.

// Anonymous Gregorian computus (returns a Date in the local timezone).
function getEasterDate( year ) {
	var a = year % 19;
	var b = Math.floor( year / 100 );
	var c = year % 100;
	var d = Math.floor( b / 4 );
	var e = b % 4;
	var f = Math.floor( ( b + 8 ) / 25 );
	var g = Math.floor( ( b - f + 1 ) / 3 );
	var h = ( 19 * a + b - d - g + 15 ) % 30;
	var i = Math.floor( c / 4 );
	var k = c % 4;
	var l = ( 32 + 2 * e + 2 * i - h - k ) % 7;
	var m = Math.floor( ( a + 11 * h + 22 * l ) / 451 );
	var month = Math.floor( ( h + l - 7 * m + 114 ) / 31 ); // 3 = March, 4 = April
	var day   = ( ( h + l - 7 * m + 114 ) % 31 ) + 1;
	return new Date( year, month - 1, day );
}

// Nth occurrence of a weekday in a month. n ∈ 1..5, dow ∈ 0..6 (Sun=0).
function getNthDow( year, month, dow, n ) {
	var first    = new Date( year, month, 1 );
	var firstDow = first.getDay();
	var offset   = ( dow - firstDow + 7 ) % 7;
	return new Date( year, month, 1 + offset + ( n - 1 ) * 7 );
}

// Last occurrence of a weekday in a month.
function getLastDow( year, month, dow ) {
	var last    = new Date( year, month + 1, 0 ); // last day of month
	var lastDow = last.getDay();
	var offset  = ( lastDow - dow + 7 ) % 7;
	return new Date( year, month, last.getDate() - offset );
}

// Monday before May 25 (Victoria Day / National Patriots' Day in QC).
function getVictoriaDay( year ) {
	// "Monday on or before May 24" — the Monday in the May 18-24 range.
	var d = new Date( year, 4, 24 ); // May 24
	while ( d.getDay() !== 1 ) {
		d.setDate( d.getDate() - 1 );
	}
	return d;
}

function makeKey( d ) {
	return d.getFullYear() + '-' + ( d.getMonth() + 1 ) + '-' + d.getDate();
}

// Federal + Quebec stat holidays. Probot is QC-based so we include St-Jean
// (June 24) which isn't observed in other provinces. Boxing Day is included
// because most maintenance teams treat it as off-day.
function getCAHolidays( year ) {
	var easter = getEasterDate( year );
	var goodFriday = new Date( easter );
	goodFriday.setDate( goodFriday.getDate() - 2 );
	var easterMonday = new Date( easter );
	easterMonday.setDate( easterMonday.getDate() + 1 );

	return [
		[ new Date( year, 0, 1 ),  'Jour de l\'An' ],
		[ goodFriday,              'Vendredi saint' ],
		[ easterMonday,            'Lundi de Pâques' ],
		[ getVictoriaDay( year ),  'Fête des Patriotes' ],
		[ new Date( year, 5, 24 ), 'Saint-Jean-Baptiste' ],
		[ new Date( year, 6, 1 ),  'Fête du Canada' ],
		[ getNthDow( year, 7, 1, 1 ),  'Fête civique' ],         // 1st Mon Aug
		[ getNthDow( year, 8, 1, 1 ),  'Fête du Travail' ],      // 1st Mon Sep
		[ getNthDow( year, 9, 1, 2 ),  'Action de grâces' ],     // 2nd Mon Oct
		[ new Date( year, 10, 11 ),    'Jour du Souvenir' ],
		[ new Date( year, 11, 25 ),    'Noël' ],
		[ new Date( year, 11, 26 ),    'Lendemain de Noël' ],
	];
}

// US federal holidays (the 11 that affect most of the labour market).
function getUSHolidays( year ) {
	return [
		[ new Date( year, 0, 1 ),       'New Year\'s Day' ],
		[ getNthDow( year, 0, 1, 3 ),   'Martin Luther King Jr. Day' ],   // 3rd Mon Jan
		[ getNthDow( year, 1, 1, 3 ),   'Presidents\' Day' ],             // 3rd Mon Feb
		[ getLastDow( year, 4, 1 ),     'Memorial Day' ],                 // last Mon May
		[ new Date( year, 5, 19 ),      'Juneteenth' ],
		[ new Date( year, 6, 4 ),       'Independence Day' ],
		[ getNthDow( year, 8, 1, 1 ),   'Labor Day' ],                    // 1st Mon Sep
		[ getNthDow( year, 9, 1, 2 ),   'Columbus Day' ],                 // 2nd Mon Oct
		[ new Date( year, 10, 11 ),     'Veterans Day' ],
		[ getNthDow( year, 10, 4, 4 ),  'Thanksgiving Day' ],             // 4th Thu Nov
		[ new Date( year, 11, 25 ),     'Christmas Day' ],
	];
}

// Memo cache so we don't recompute the same year's table 90× per render.
var holidayCache = {};

function getHolidayMap( year, country ) {
	var key = country + ':' + year;
	if ( holidayCache[ key ] ) return holidayCache[ key ];
	var list = country === 'US' ? getUSHolidays( year ) : getCAHolidays( year );
	var map  = {};
	list.forEach( function ( pair ) { map[ makeKey( pair[ 0 ] ) ] = pair[ 1 ]; } );
	holidayCache[ key ] = map;
	return map;
}

// Returns the holiday name for a date, or null. Country defaults to 'CA'
// when the address hasn't been resolved yet so the calendar still gets
// useful markers in the most common case.
export function isHoliday( date, country ) {
	if ( ! date ) return null;
	var c = ( country === 'US' ) ? 'US' : 'CA';
	var map = getHolidayMap( date.getFullYear(), c );
	return map[ makeKey( date ) ] || null;
}
